import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { naira } from "@/lib/helpers";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Users, Car, Crown, ScanLine, TrendingUp, ArrowLeft, LayoutDashboard, Radio } from "lucide-react";

export default function Admin() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [scans, setScans] = useState([]);
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/admin/scans").then((r) => setScans(r.data)).catch(() => {});
    api.get("/admin/transactions").then((r) => setTxns(r.data)).catch(() => {});
  }, []);

  const cards = stats ? [
    { icon: Users, label: "Total Users", value: stats.users, tone: "text-emerald-600 bg-emerald-50" },
    { icon: Crown, label: "Premium", value: stats.premium_users, tone: "text-amber-600 bg-amber-50" },
    { icon: Car, label: "Vehicles", value: stats.vehicles, tone: "text-blue-600 bg-blue-50" },
    { icon: ScanLine, label: "AI Scans", value: stats.scans, tone: "text-indigo-600 bg-indigo-50" },
    { icon: TrendingUp, label: "Revenue", value: naira(stats.revenue), tone: "text-emerald-600 bg-emerald-50" },
    { icon: TrendingUp, label: "Conversion", value: `${stats.conversion}%`, tone: "text-zinc-600 bg-zinc-100" },
  ] : [];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <Logo showText={false} />
            <div className="flex items-center gap-2"><LayoutDashboard size={18} className="text-zinc-400" /><span className="font-display font-bold">Admin</span></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" data-testid="admin-live-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 gap-1.5" onClick={() => navigate("/admin/live")}><Radio size={14} /> Live</Button>
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={() => navigate("/app")}><ArrowLeft size={14} /> App</Button>
            <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200" onClick={async () => { await logout(); navigate("/"); }}>Log out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <h1 className="font-display text-2xl font-bold tracking-tight">Overview</h1>
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {!stats ? [...Array(6)].map((_, i) => <Skeleton key={i} className="h-28 rounded-3xl" />) :
            cards.map((c) => (
              <div key={c.label} className="rounded-3xl border border-zinc-200 bg-white p-5" data-testid="admin-stat">
                <div className={`grid h-10 w-10 place-items-center rounded-xl ${c.tone}`}><c.icon size={20} /></div>
                <div className="mt-3 font-display text-xl font-bold">{c.value}</div>
                <div className="text-xs text-zinc-400">{c.label}</div>
              </div>
            ))}
        </div>

        <Tabs defaultValue="users" className="mt-8">
          <TabsList className="rounded-full bg-zinc-100 p-1">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full data-[state=active]:bg-white">Users</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="admin-tab-vehicles" className="rounded-full data-[state=active]:bg-white">Vehicles</TabsTrigger>
            <TabsTrigger value="scans" data-testid="admin-tab-scans" className="rounded-full data-[state=active]:bg-white">Scans</TabsTrigger>
            <TabsTrigger value="txns" data-testid="admin-tab-txns" className="rounded-full data-[state=active]:bg-white">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-5">
            <div className="overflow-x-auto rounded-3xl border border-zinc-200 bg-white">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Plan</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}><TableCell className="font-medium">{u.name}</TableCell><TableCell className="text-zinc-500">{u.email}</TableCell>
                      <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.plan === "premium" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>{u.plan}</span></TableCell>
                      <TableCell className="text-zinc-500">{u.role}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="vehicles" className="mt-5">
            <div className="overflow-x-auto rounded-3xl border border-zinc-200 bg-white">
              <Table>
                <TableHeader><TableRow><TableHead>Make/Model</TableHead><TableHead>Year</TableHead><TableHead>Plate</TableHead><TableHead>Mileage</TableHead><TableHead>Health</TableHead></TableRow></TableHeader>
                <TableBody>
                  {vehicles.map((v) => (
                    <TableRow key={v.id}><TableCell className="font-medium">{v.make} {v.model}</TableCell><TableCell>{v.year}</TableCell><TableCell>{v.plate}</TableCell><TableCell>{v.mileage?.toLocaleString()} km</TableCell><TableCell className="font-bold text-emerald-600">{v.health_score}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="scans" className="mt-5">
            <div className="overflow-x-auto rounded-3xl border border-zinc-200 bg-white">
              <Table>
                <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Problem</TableHead><TableHead>Severity</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>
                  {scans.map((s) => (
                    <TableRow key={s.id}><TableCell className="capitalize font-medium">{s.category}</TableCell><TableCell className="text-zinc-500 max-w-xs truncate">{s.result?.problem}</TableCell><TableCell>{s.result?.severity}</TableCell><TableCell className="text-zinc-400">{new Date(s.created_at).toLocaleDateString()}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="txns" className="mt-5">
            <div className="overflow-x-auto rounded-3xl border border-zinc-200 bg-white">
              <Table>
                <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Plan</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {txns.map((t) => (
                    <TableRow key={t.reference}><TableCell className="font-mono text-xs">{t.reference}</TableCell><TableCell>{t.plan}</TableCell><TableCell>{naira(t.amount)}</TableCell>
                      <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${t.status === "success" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{t.status}</span></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
